import { useCallback, useEffect, useRef, useState } from 'react';
import parentFrameMessaging from '../services/ParentFrameMessaging';

interface AddToCartResult {
  cartId: string | null;
  cartItem: any | null;
  loading: boolean;
  error: string | null;
}

// Singleton state to share across all instances
let sharedState: AddToCartResult = {
  cartId: null,
  cartItem: null,
  loading: false,
  error: null,
};

let pendingRequest = false;
let stateChangeListeners: Set<(state: AddToCartResult) => void> = new Set();
let listenersSetup = false;

// Function to notify all instances of state changes
function notifyStateChange() {
  stateChangeListeners.forEach((listener) => listener(sharedState));
}

// Function to update shared state
function updateSharedState(newState: Partial<AddToCartResult>) {
  sharedState = { ...sharedState, ...newState };
  notifyStateChange();
}

// Setup listeners only once
function setupListeners() {
  if (listenersSetup) return;

  parentFrameMessaging.onAddToCartSuccess((data) => {
    if (pendingRequest) {
      updateSharedState({
        cartId: data.cartId || null,
        cartItem: data.cartItem || null,
        loading: false,
        error: null,
      });
      pendingRequest = false;
    }
  });

  parentFrameMessaging.onAddToCartError((data) => {
    if (pendingRequest) {
      updateSharedState({
        cartId: null,
        cartItem: null,
        loading: false,
        error: data.error || 'Unknown error',
      });
      pendingRequest = false;
    }
  });

  listenersSetup = true;
}

export function useCartMessaging() {
  const [localState, setLocalState] = useState<AddToCartResult>(sharedState);

  // Setup listeners on first use
  useEffect(() => {
    setupListeners();

    // Register this component to receive state updates
    const updateLocalState = (newState: AddToCartResult) => {
      setLocalState(newState);
    };

    stateChangeListeners.add(updateLocalState);

    // Sync with current shared state
    setLocalState(sharedState);

    return () => {
      stateChangeListeners.delete(updateLocalState);
    };
  }, []);

  // Function to send add-to-cart message
  const addToCart = useCallback((variantId: number, quantity: number = 1) => {
    updateSharedState({ cartId: null, cartItem: null, loading: true, error: null });
    pendingRequest = true;
    parentFrameMessaging.sendAddToCart(variantId, quantity);
  }, []);

  return { ...localState, addToCart };
}
